<template>
  <div>
    <div v-if="loading > 0 || requesting" class="block-wizard__loading">
      <LoadingComponent :text="$t('survey.loadingInProgress')"></LoadingComponent>
    </div>
    <template>
      <form-wizard
        @on-complete="onComplete"
        @on-loading="onLoading"
        @on-change="nextTab"
        color="#0D93D2"
        error-color="#E62243"
        title=""
        subtitle=""
        back-button-text="Précédent"
        next-button-text="Suivant"
        finishButtonText="Valider"
        ref="formWizard">
        <tab-content
          v-if="updateId === undefined"
          :title="$t('survey.bceNumber')"
          :before-change="validateFirstTab"
          icon="fa fa-building">
          <p>{{ $t("survey.pleaseBce") }}</p>
          <vue-form-generator
            :model="model"
            :schema="firstTabSchema"
            :options="formOptions"
            ref="firstTabForm">
          </vue-form-generator>
        </tab-content>
        <tab-content
          :title="$t('survey.principalActivitySector')"
          :before-change="validateSecondTab"
          icon="fa fa-cogs">
          <div v-if="enterpriseName" class="content">
            <h6>{{ $t("survey.whichEnterprise") }}</h6>
            <p>{{ enterpriseName }}</p>
          </div>
          <p v-if="this.noSectorFind">{{ this.noSectorFind }}</p>
          <vue-form-generator
            :model="model"
            :schema="secondTabSchema"
            :options="formOptions"
            ref="secondTabForm">
          </vue-form-generator>
        </tab-content>
        <tab-content
          v-if="updateId === undefined"
          :title="$t('survey.shareData')"
          icon="fa fa-share-alt">
          <p>{{ $t('survey.shareDataExplain') }} {{ $t('survey.shareDataExplainCreating') }}<br />{{ $t('survey.shareDataExplainMaximum') }}</p>
          <ShareList :partners="shareListPartners"></ShareList>
        </tab-content>
      </form-wizard>
      <div
        class="block-cta__content-button divButtonSurvey"
        @click="cancel"
        v-if="canCancel">
        <button class="btn btn-fullwidth">{{ $t("survey.cancel") }}</button>
      </div>
    </template>
  </div>
</template>

<script>
/* eslint-disable object-shorthand */
/* eslint-disable func-names */
import _ from 'lodash';
import VueFormGenerator from 'vue-form-generator';
import LoadingComponent from '@/components/Loading.vue';
import { getEnterpriseByBCE } from '@/providers/enterprise';
import { listYEnterpriseSectorUsers } from '@/providers/yEnterpriseSectorUsers';
import { getSectors } from '@/providers/sectors';
import { listDigiscorePartners } from '@/providers/partners';
import ShareList from '@/components/SurveyList/ShareList.vue';

// eslint-disable-next-line func-names
VueFormGenerator.validators.isValidBce = function (value) {
  let enterpriseNumber = value;
  enterpriseNumber = this.$lodash.replace(enterpriseNumber, /[^0-9]+/g, '');
  const validBceNumber = /^[0-9]*$/;
  if (validBceNumber.test(enterpriseNumber)) {
    this.model.bceNumber = enterpriseNumber;
    return '';
  }
  return 'Is not a valid Bce Number';
};

export default {
  name: 'SurveyForm',
  props: ['requesting'],
  components: {
    'vue-form-generator': VueFormGenerator.component,
    LoadingComponent,
    ShareList,
  },
  data() {
    return {
      loading: 0,
      canCancel: true,
      alreadyExistingBCE: [],
      alreadyExists: false,
      enterpriseData: undefined,
      sectorsList: [],
      originalBceNumber: '',
      model: {
        bceNumber: '',
      },
      enterpriseName: '',
      yEnterpriseSectorUsers: [],
      noSectorFind: '',
      resJSONBCE: '',
      updateId: undefined,
      sectActiName: '',
      activity: [],
      activitySectorText: [],
      activitySector: '',
      activitySectorListText: [],
      shareListPartners: [],
      firstTabSchema: {
        fields: [{
          type: 'input',
          inputType: 'text',
          label: `${this.$t('survey.bceNumber')} <span class="c-tag red">requis</span>`,
          model: 'bceNumber',
          id: 'bceNumber',
          required: true,
          validator: VueFormGenerator.validators.isValidBce,
          styleClasses: '',
          hint: this.$t('survey.bceNumberHint'),
        }],
      },
      secondTabSchema: {
        fields: [{
          type: 'radios',
          values: [],
          label: 'Quel est le secteur d’activité principal de l’entreprise ? <span class="c-tag red">requis</span>',
          model: 'activitySector',
          id: 'activitySector',
          required: true,
          styleClasses: 'display: block;',
          radiosOptions: {
            value: 'id',
            name: 'name',
          },
          validator: VueFormGenerator.validators.required,
        },
        {
          type: 'select',
          values: [],
          label: this.$t('survey.chooseOther'),
          model: 'activitySectorList',
          id: 'activitySectorList',
          required: true,
          validator: VueFormGenerator.validators.number.locale({
            fieldIsRequired: '',
          }),
          styleClasses: '',
          selectOptions: {
            noneSelectedText: this.$t('survey.noneSelectedText'),
          },
          visible: false,
        }],
      },
      formOptions: {
        validationErrorClass: 'has-error',
        validationSuccessClass: 'has-success',
        validateAfterChanged: true,
      },
    };
  },
  async mounted() {
    this.loading += 1;
    this.updateId = undefined;

    await Promise.all([
      (async () => {
        const { data: sectors } = await getSectors();
        if (!_.isEmpty(sectors)) {
          this.sectorsList = _.sortBy(sectors, 'Code');
        }
      })(),
      (async () => {
        const { data: yEnterpriseSectorUsers } = await listYEnterpriseSectorUsers();
        this.yEnterpriseSectorUsers = yEnterpriseSectorUsers;
        this.canCancel = (yEnterpriseSectorUsers && yEnterpriseSectorUsers.length > 0);
        this.alreadyExistingBCE = yEnterpriseSectorUsers
          .filter((yesu) => yesu.surveys.some((survey) => survey.userId === this.$auth.user.id))
          .map((yesu) => yesu.EnterpriseNumber);

        if (this.$route.name === 'EditASurvey' && this.$route.params.id) {
          this.updateId = this.$route.params.id;
          // eslint-disable-next-line no-underscore-dangle
          this.enterpriseData = yEnterpriseSectorUsers.find((yesu) => yesu._id === this.updateId);
          this.model.bceNumber = this.enterpriseData.EnterpriseNumber;
          this.originalBceNumber = this.model.bceNumber;

          await this.validateFirstTab();
          this.nextTab(0);
          this.$refs.formWizard.changeTab(0, 0);
        }
      })(),
      (async () => {
        const { data: reponseApiPartners } = await listDigiscorePartners();
        this.shareListPartners = reponseApiPartners.map((partner) => ({ ...partner, active: false }));
      })(),
    ]);

    this.loading -= 1;
  },
  methods: {
    async validateFirstTab() {
      const fielList = this.secondTabSchema.fields.find((field) => field.model === 'activitySectorList');
      fielList.visible = false;
      const fielListActivitySector = this.secondTabSchema.fields.find((field) => field.model === 'activitySector');
      fielListActivitySector.visible = true;
      this.activitySectorText = [];
      let response = false;
      this.loading += 1;
      if (this.checkBceNumber(this.model.bceNumber)) {
        if (this.alreadyExistingBCE.filter((existingBCE) => existingBCE !== this.originalBceNumber).includes(this.model.bceNumber)) {
          this.alreadyExists = true;
          this.enterpriseData = this.yEnterpriseSectorUsers.find((yesu) => yesu.EnterpriseNumber === this.model.bceNumber);
          // this.updateId = _.get(this.enterpriseData, '_id');
          // console.log('found enterpriseData', this.enterpriseData, this.enterpriseData.survey.startedDate, this.enterpriseData.survey.lastUpdatedAt);
          // const startedDate = this.enterpriseData.survey.startedDate ? this.enterpriseData.survey.startedDate : this.enterpriseData.survey.lastUpdatedAt;
          // && addDays(new Date(startedDate), 15) > new Date()
          if (this.enterpriseData.surveys.some((survey) => survey.status === 'InProgress' && !survey.archived)) {
            this.$emit('error', 'surveyAlreadyInProgress');
            this.loading -= 1;
            return false;
          }
        }
        try {
          const { data: enterprise } = await getEnterpriseByBCE(this.model.bceNumber);
          this.resJSONBCE = enterprise;
          let countOption = 1;
          if (_.isEmpty(_.isArray(_.get(this.resJSONBCE, 'Activity')))) {
            this.enterpriseName = _.get(_.head(_.get(this.resJSONBCE, 'Denomination')), 'Value');
            this.activity = _.get(this.resJSONBCE, 'Activity');
            this.activity = _.uniqBy(this.activity, 'Sector.Code');
            this.activity = _.sortBy(this.activity, 'Sector.Code');
            this.noSectorFind = this.$t('survey.noSectorFind');
            _.forEach(this.activity, (value) => {
              if (value?.Sector?.Code !== 'EXC' && value?.Sector?.Code !== 'S15' && value?.Sector?.Name) {
                this.activitySectorText.push({
                  id: countOption,
                  name: value?.Sector?.Name,
                });
                countOption += 1;
                this.noSectorFind = '';
              }
            });
            this.activitySectorText.push({
              id: 999,
              name: 'Autre',
            });
            const fiel = this.secondTabSchema.fields.find((field) => field.model === 'activitySector');
            if (this.noSectorFind !== '') {
              fiel.visible = false;
              this.secondTabSchema.fields[1].visible = true;
            } else {
              fiel.visible = true;
              this.secondTabSchema.fields[1].visible = false;
            }
            if (_.isEmpty(this.activity)) {
              this.secondTabSchema.fields[1].label = '';
            } else {
              this.secondTabSchema.fields[1].label = this.$t('survey.chooseOther');
            }
            fiel.values = this.activitySectorText;
            const elements = document.getElementsByName('activitySector');
            for (let j = 0; j < elements.length; j += 1) {
              elements[j].checked = false;
            }
            response = true;
            this.$emit('clearError');
          }
        } catch (err) {
          this.$emit('error', 'retrieveEnterprise');
        }
      }


      if (this.enterpriseData && this.enterpriseData.SectorCode) {
        const activitySectorName = this.sectorsList.find((s) => s.Code === this.enterpriseData.SectorCode).Name;
        if (activitySectorName !== undefined) {
          const activitySector = this.activitySectorText.find((a) => a.name === activitySectorName);
          if (activitySector !== undefined) {
            this.model.activitySector = activitySector.id;
          }
        }
      }
      this.loading -= 1;

      return response;
    },
    validateSecondTab() {
      this.activitySector = document.querySelector('input[name=activitySector]:checked');
      if (this.activitySector !== null || (this.activitySector === null && document.getElementById('activitySectorList') !== null)) {
        if (document.getElementById('activitySectorList') !== null) {
          if (document.getElementById('activitySectorList').value === '') {
            return false;
          }
        }
        if (this.activitySector !== null) {
          this.activitySector = this.activitySector.value;
        }
        this.sectActiName = '';
        const fielList = this.secondTabSchema.fields.find((field) => field.model === 'activitySectorList');
        if (fielList.visible) {
          this.activitySectorList = document.getElementById('activitySectorList').value;
          this.sectActiName = this.sectorAcivityNameList;
        } else {
          this.sectActiName = this.sectorAcivityName;
        }
        return this.$refs.secondTabForm.validate();
      }
      return false;
    },
    nextTab(v) {
      if (v === 0 || v === 2) {
        this.activitySectorListText = [];
        let countOption = 1;
        if (_.isEmpty(_.isArray(this.sectorsList))) {
          _.forEach(this.sectorsList, (value) => {
            if (value.Code !== 'EXC' && value.Code !== 'S15' && value?.Name) {
              this.activitySectorListText.push({
                id: countOption,
                name: value.Name,
              });
              countOption += 1;
            }
          });
          _.forEach(this.activitySectorText, (val) => {
            if (_.find(this.activitySectorListText, (o) => val.name === o.name)) {
              _.remove(this.activitySectorListText, {
                name: val.name,
              });
            }
          });
          const fielList = this.secondTabSchema.fields.find((field) => field.model === 'activitySectorList');
          fielList.values = this.activitySectorListText;
          if (this.noSectorFind !== '') {
            fielList.visible = true;
          }
          const radios = document.getElementsByName('activitySector');
          for (let i = 0; i < radios.length; i += 1) {
            radios[i].onclick = async (e) => {
              if (e.target.value === '999') {
                fielList.visible = true;
                const testFieldList = await fielList.visible;
                const select = document.getElementById('activitySectorList');
                if (testFieldList && select !== null) {
                  select.selectedIndex = 0;
                }
              } else {
                fielList.visible = false;
              }
            };
          }
        }
      }
    },
    checkBceNumber(bce) {
      if (bce < 100000000) {
        return false;
      }
      const modulo = 97;
      const verif = Math.floor(bce / 100);
      const digit = bce % 100;
      const check = modulo - (verif % modulo);
      return (check === digit);
    },
    onComplete() {
      const sectActi = _.get(_.find(this.sectorsList, (o) => o.Name === this.sectActiName), 'Code');
      const surveySharedWith = (this.updateId === undefined) ? { sharedWith: this.shareListPartners.filter((partner) => partner.active).map((partner) => partner.id) } : undefined;
      const data = {
        _id: _.get(this.enterpriseData, '_id'),
        EnterpriseNumber: this.model.bceNumber,
        SectorCode: sectActi,
        Name: this.enterpriseName,
        Namespace: 'BaaS22E',
        Address: _.get(this.resJSONBCE, 'Address'),
        survey: surveySharedWith,
      };
      this.$emit('completed', data);
    },
    onLoading(loading) {
      if (loading) {
        this.$emit('ready', true);
      }
    },
    cancel() {
      this.$emit('canceled');
    },
  },
  computed: {
    sectorAcivityName() {
      return _.get(_.find(this.activitySectorText, (o) => parseInt(o.id, 10) === parseInt(this.activitySector, 10)), 'name');
    },
    sectorAcivityNameList() {
      return _.get(_.find(this.activitySectorListText, (o) => parseInt(o.id, 10) === parseInt(this.activitySectorList, 10)), 'name');
    },
  },
};
</script>

<style>
.field label {
  margin-right: 40px;
}
.vue-form-generator span{
  color: #2368a1;
  font-weight: 700;
  margin-top: 32px;
  font-size: 1.1em;
}
.vue-form-generator span .c-tag {
  color: white;
  font-weight: 700;
  margin-top: 0px;
  font-size: 10px;
}
input[type=radio] {
  border: 0px;
  width: 100%;
  height: 2em;
}
select {
  font-family: inherit;
}
.wizard-btn {
  margin-top: 30px;
}
.divButtonSurvey {
  margin-top: 30px;
}
.block-wizard__loading {
  position: absolute;
  width: 100vw;
  height: 100vh;
  margin-top: 228px;
  padding-top: 228px;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: top;
  top: 100%;
}
</style>
